import { useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { colors } from '@yorukiri/ui/tokens';
import { ChevronDown, ChevronRight } from './icons';
import { useExpandedChildren, useMovePage, type ApiPageSummary } from '../lib/queries';

type FlatNode = { page: ApiPageSummary; depth: number; hasChildren: boolean };

type DropIntent =
  | { kind: 'before'; targetId: string }
  | { kind: 'child'; targetId: string }
  | { kind: 'root' };

// Midpoint between neighbors (fractional). Falls back to Date.now() which is
// always larger than backfilled positions, so append is safe even for parents
// whose children haven't been loaded yet.
function computePosition(before: number | null, after: number | null): number {
  if (before == null && after == null) return Date.now();
  if (before == null) return (after ?? 0) - 1024;
  if (after == null) return before + 1024;
  return (before + after) / 2;
}

function flatten(
  byParent: Map<string | null, ApiPageSummary[]>,
  expanded: Set<string>,
): FlatNode[] {
  const out: FlatNode[] = [];
  const walk = (parentId: string | null, depth: number) => {
    for (const page of byParent.get(parentId) ?? []) {
      out.push({ page, depth, hasChildren: page.hasChildren });
      if (expanded.has(page.id) && byParent.has(page.id)) walk(page.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

export function PageTree({ rootPages }: { rootPages: ApiPageSummary[] }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [activeId, setActiveId] = useState<string | null>(null);
  const [intent, setIntent] = useState<DropIntent | null>(null);
  const movePage = useMovePage();

  const expandedIds = useMemo(() => [...expanded], [expanded]);
  const childrenResults = useExpandedChildren(expandedIds);

  const byParent = useMemo(() => {
    const map = new Map<string | null, ApiPageSummary[]>();
    map.set(
      null,
      [...rootPages].sort((a, b) => a.position - b.position),
    );
    expandedIds.forEach((id, i) => {
      const data = childrenResults[i]?.data;
      if (data) map.set(id, [...data].sort((a, b) => a.position - b.position));
    });
    return map;
  }, [rootPages, expandedIds, childrenResults]);

  const allLoaded = useMemo(() => [...byParent.values()].flat(), [byParent]);
  const byId = useMemo(() => new Map(allLoaded.map((p) => [p.id, p])), [allLoaded]);
  const flat = useMemo(() => flatten(byParent, expanded), [byParent, expanded]);
  const flatIds = flat.map((f) => f.page.id);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const activePage = activeId ? byId.get(activeId) ?? null : null;

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const isDescendant = (ancestor: string, candidate: string): boolean => {
    let current = byId.get(candidate);
    while (current?.parentPageId) {
      if (current.parentPageId === ancestor) return true;
      current = byId.get(current.parentPageId);
    }
    return false;
  };

  const siblingsOf = (parentId: string | null) => byParent.get(parentId) ?? [];

  const onDragStart = (e: DragStartEvent) => {
    setActiveId(String(e.active.id));
    setIntent(null);
  };

  const onDragOver = (e: DragOverEvent) => {
    const { active, over } = e;
    if (!over || !active) return setIntent(null);
    const activeIdStr = String(active.id);
    const overId = String(over.id);
    if (overId === 'page-tree-root') return setIntent({ kind: 'root' });
    if (overId === activeIdStr) return setIntent(null);
    const overRect = over.rect;
    const pointerX = e.activatorEvent instanceof MouseEvent ? e.activatorEvent.clientX : 0;
    const delta = e.delta?.x ?? 0;
    const relX = pointerX + delta - overRect.left;
    if (relX > 32) setIntent({ kind: 'child', targetId: overId });
    else setIntent({ kind: 'before', targetId: overId });
  };

  const onDragEnd = async (_e: DragEndEvent) => {
    const active = activeId;
    setActiveId(null);
    const currentIntent = intent;
    setIntent(null);
    if (!active || !currentIntent) return;

    const activePage = byId.get(active);
    if (!activePage) return;

    let targetParentId: string | null;
    let targetIndex: number;

    if (currentIntent.kind === 'root') {
      targetParentId = null;
      const siblings = siblingsOf(null).filter((p) => p.id !== active);
      targetIndex = siblings.length;
    } else if (currentIntent.kind === 'child') {
      if (currentIntent.targetId === active || isDescendant(active, currentIntent.targetId)) return;
      targetParentId = currentIntent.targetId;
      const siblings = siblingsOf(currentIntent.targetId).filter((p) => p.id !== active);
      targetIndex = siblings.length;
      // auto-expand drop target
      setExpanded((prev) => {
        if (prev.has(currentIntent.targetId)) return prev;
        const next = new Set(prev);
        next.add(currentIntent.targetId);
        return next;
      });
    } else {
      const target = byId.get(currentIntent.targetId);
      if (!target) return;
      if (target.id === active || isDescendant(active, target.id)) return;
      targetParentId = target.parentPageId;
      const siblings = siblingsOf(targetParentId).filter((p) => p.id !== active);
      targetIndex = siblings.findIndex((p) => p.id === target.id);
      if (targetIndex < 0) targetIndex = 0;
    }

    const siblings = siblingsOf(targetParentId).filter((p) => p.id !== active);
    const before = targetIndex > 0 ? siblings[targetIndex - 1]!.position : null;
    const after = targetIndex < siblings.length ? siblings[targetIndex]!.position : null;
    const position = computePosition(before, after);

    movePage.mutate({ id: active, parentPageId: targetParentId, position });
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={() => {
        setActiveId(null);
        setIntent(null);
      }}
    >
      <SortableContext items={flatIds} strategy={verticalListSortingStrategy}>
        <div>
          {flat.map((node) => (
            <PageNodeItem
              key={node.page.id}
              node={node}
              expanded={expanded.has(node.page.id)}
              onToggle={() => toggle(node.page.id)}
              intent={intent?.kind !== 'root' && intent?.targetId === node.page.id ? intent : null}
              draggingId={activeId}
            />
          ))}
        </div>
      </SortableContext>
      <RootDropZone active={Boolean(activeId)} highlighted={intent?.kind === 'root'} />
      <DragOverlay>
        {activePage && (
          <div style={dragOverlay}>
            <span
              style={{
                ...dot,
                background: activePage.color ?? colors.line2,
              }}
            />
            {activePage.title || 'Untitled'}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

function PageNodeItem({
  node,
  expanded,
  onToggle,
  intent,
  draggingId,
}: {
  node: FlatNode;
  expanded: boolean;
  onToggle: () => void;
  intent: DropIntent | null;
  draggingId: string | null;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: node.page.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
    position: 'relative',
  };

  const showBeforeLine = intent?.kind === 'before';
  const showChildHighlight = intent?.kind === 'child';
  const dragging = draggingId === node.page.id;

  return (
    <div ref={setNodeRef} style={style}>
      {showBeforeLine && <span style={beforeLine} />}
      <NavLink
        to={`/pages/${node.page.id}`}
        style={(state) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '5px 8px',
          paddingLeft: 8 + node.depth * 14,
          borderRadius: 5,
          color: state.isActive ? colors.surface : colors.ink2,
          background: showChildHighlight
            ? colors.accentSoft
            : state.isActive
              ? colors.accent
              : 'transparent',
          textDecoration: 'none',
          cursor: dragging ? 'grabbing' : 'default',
        })}
      >
        <span
          onClick={
            node.hasChildren
              ? (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onToggle();
                }
              : undefined
          }
          style={{
            width: 12,
            height: 12,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: node.hasChildren ? 'pointer' : 'default',
            opacity: node.hasChildren ? 0.7 : 0,
            flexShrink: 0,
          }}
        >
          {node.hasChildren ? (
            expanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />
          ) : null}
        </span>
        <span
          {...attributes}
          {...listeners}
          style={{
            ...dot,
            background: node.page.color ?? colors.line2,
            cursor: 'grab',
          }}
        />
        <span
          {...attributes}
          {...listeners}
          style={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            cursor: 'grab',
          }}
        >
          {node.page.title || 'Untitled'}
        </span>
      </NavLink>
    </div>
  );
}

function RootDropZone({ active, highlighted }: { active: boolean; highlighted: boolean }) {
  const { setNodeRef } = useDroppable({ id: 'page-tree-root' });
  if (!active) return null;
  return (
    <div
      ref={setNodeRef}
      style={{
        height: 22,
        marginTop: 6,
        borderRadius: 4,
        border: `1px dashed ${highlighted ? colors.accent : colors.line2}`,
        background: highlighted ? colors.accentSoft : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontVariantNumeric: 'tabular-nums',
        fontSize: 10,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: highlighted ? colors.accent : colors.ink3,
      }}
    >
      Drop to move to root
    </div>
  );
}

const beforeLine: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 8,
  right: 8,
  height: 2,
  background: colors.accent,
  borderRadius: 1,
  pointerEvents: 'none',
};

const dragOverlay: React.CSSProperties = {
  background: colors.surface,
  border: `1px solid ${colors.line2}`,
  borderRadius: 6,
  padding: '5px 10px',
  fontSize: 12.5,
  color: colors.ink,
  boxShadow: '0 10px 24px -12px rgba(24,22,15,0.25)',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};

const dot: React.CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  flexShrink: 0,
  display: 'inline-block',
};
